import React from 'react';
import { useAuthQuery as useQuery } from '../../../../shared/hooks/useAuthQuery';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../../../convex/_generated/api';
import { Id } from '../../../../convex/_generated/dataModel';
import { Button } from '../../../../catalyst-components/button';
import { Spinner } from '../../../../catalyst-components/spinner';
import { Badge } from '../../../../catalyst-components/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../../catalyst-components/table';
import { formatDateWithTime } from '../../../../shared/helpers';
import { ArrowLeftIcon, EyeIcon, ChartBarIcon } from '@heroicons/react/24/outline';

export const AssistantSessionsDetail: React.FC = () => {
  const navigate = useNavigate();
  const { assistantId } = useParams<{ assistantId: string }>();

  const result = useQuery(
    api.sessions.getSessionsByAssistantPaginated,
    assistantId ? { assistantId: assistantId as Id<"assistants"> } : "skip"
  );

  const formatDuration = (durationMs: number) => {
    if (durationMs === 0) return 'N/A';
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  if (!result) {
    return (
      <div className="min-h-screen bg-background pt-24">
        <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin/sessions')}>
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                Back to Sessions
              </Button>
            </div>
            <h1 className="text-3xl font-semibold text-foreground">Loading...</h1>
          </div>
        </div>
        <div className="flex justify-center items-center h-64">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24">
      {/* Header */}
      <div className="border-b border-border/50 bg-gradient-to-r from-green-50 to-green-100/50 px-6 py-8">
        <div>
          <div className="flex items-center gap-4 mb-4">
            <Button color="secondary" className="rounded-full" onClick={() => navigate('/admin/sessions')}>
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Sessions
            </Button>
          </div>
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-white border-2 border-green-200">
              <ChartBarIcon className="h-8 w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-foreground mb-1">{result.assistantName}</h1>
              <p className="text-base text-muted-foreground">
                {result.sessions.length} session{result.sessions.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-8">
        <div className="space-y-6">
          {/* Table */}
          <div className="border-2 border-border/50 rounded-xl overflow-hidden bg-card">
            <Table>
              <TableHead>
                <TableRow className="bg-muted/30">
                  <TableHeader>User</TableHeader>
                  <TableHeader>Email</TableHeader>
                  <TableHeader>Started</TableHeader>
                  <TableHeader>Duration</TableHeader>
                  <TableHeader>Messages</TableHeader>
                  <TableHeader>Status</TableHeader>
                  <TableHeader>Actions</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.sessions.map((session) => (
                  <TableRow key={session.sessionId} className="hover:bg-muted/20">
                    <TableCell>
                      <span className="text-sm font-medium text-foreground">{session.userName}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{session.userEmail}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatDateWithTime(session.creationTime)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{formatDuration(session.duration)}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">{session.messageCount}</span>
                    </TableCell>
                    <TableCell>
                      {session.isComplete ? (
                        <Badge color="green">Completed</Badge>
                      ) : (
                        <Badge color="yellow">In Progress</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          color="secondary"
                          className="rounded-full"
                          onClick={() => navigate(`/chat?sessionId=${session.sessionId}`)}
                        >
                          <EyeIcon className="h-4 w-4 mr-1" />
                          View Session
                        </Button>
                        {session.isComplete && (
                          <Button
                            color="primary"
                            className="rounded-full"
                            onClick={() => navigate(`/admin/sessions/${session.sessionId}/insights?from=sessions&userId=${session.userId}`)}
                          >
                            <ChartBarIcon className="h-4 w-4 mr-1" />
                            Insights
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {result.sessions.length === 0 && (
            <div className="text-center py-12 text-muted-foreground bg-muted/30 rounded-xl">
              No sessions found for this assistant
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
